<template>
  <div class="tw-space-y-6">
    <div
      v-if="this.$apollo.queries.contractRates.loading"
      class="tw-text-sm tw-text-slate-500"
    >
      Loading rates...
    </div>

    <div v-else class="tw-space-y-6">
      <section class="tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white tw-shadow-sm">
        <div
          class="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-slate-100 tw-px-5 tw-py-4"
        >
          <h3 class="tw-text-lg tw-font-semibold tw-text-slate-900">Rates</h3>
          <Button v-if="!isAddingRate" @click="toggleAddContractRate">
            Add New Rate
          </Button>
        </div>

        <div
          v-if="isAddingRate"
          class="tw-grid tw-grid-cols-1 tw-gap-4 tw-border-b tw-border-slate-100 tw-bg-slate-50/60 tw-p-5 md:tw-grid-cols-[1fr_220px_auto] md:tw-items-end"
        >
          <EditableInputLabel
            :isEditing="true"
            label="City Name"
            v-model="newRate.destinationCity"
          />
          <EditableInputLabel
            :isEditing="true"
            label="Rate Amount"
            v-model="newRate.amount"
          />
          <div class="tw-flex tw-gap-2">
            <Button :isGreen="true" icon="check" @click="confirmContractRateAddition">Save</Button>
            <Button :isRed="true" :isSecondary="true" @click="toggleAddContractRate">Cancel</Button>
          </div>
        </div>

        <div v-if="contractRates && contractRates.length > 0" class="tw-overflow-x-auto">
          <table class="tw-min-w-full tw-divide-y tw-divide-slate-100">
            <thead class="tw-bg-slate-50">
              <tr>
                <th
                  class="tw-px-5 tw-py-3 tw-text-left tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500"
                >
                  City
                </th>
                <th
                  class="tw-px-5 tw-py-3 tw-text-left tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500"
                >
                  Charge Rate
                </th>
                <th
                  class="tw-px-5 tw-py-3 tw-text-right tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="tw-divide-y tw-divide-slate-100">
              <tr v-for="rate in contractRates" :key="rate.id" class="tw-align-middle">
                <td class="tw-px-5 tw-py-4 tw-align-middle">
                  <EditableInputLabel
                    :label="editingRate.id === rate.id ? 'Rate Name' : undefined"
                    :isEditing="editingRate.id === rate.id"
                    :model-value="
                      editingRate.id === rate.id
                        ? editingRate.destinationCity
                        : rate.destinationCity
                    "
                    @update:model-value="editingRate.destinationCity = $event"
                  />
                </td>
                <td
                  v-if="editingRate.id !== rate.id"
                  class="tw-px-5 tw-py-4 tw-align-middle tw-text-sm tw-font-medium tw-text-slate-800"
                >
                  <div class="tw-flex tw-items-center tw-leading-5">
                    {{ rateChargeString(rate) }}
                  </div>
                </td>
                <td v-else class="tw-px-5 tw-py-4 tw-align-middle">
                  <EditableInputLabel
                    :isEditing="true"
                    label="Rate Amount"
                    v-model="editingRate.amount"
                  />
                </td>
                <td class="tw-px-5 tw-py-4 tw-align-middle">
                  <div
                    v-if="editingRate.id === rate.id"
                    class="tw-flex tw-justify-end tw-gap-2"
                  >
                    <Button :isGreen="true" icon="check" @click="editRateCategory">Save</Button>
                    <Button :isRed="true" :isSecondary="true" @click="cancelEditRateCategory">
                      Cancel
                    </Button>
                  </div>
                  <div v-else class="tw-flex tw-justify-end tw-gap-1.5">
                    <button
                      type="button"
                      class="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-text-slate-500 hover:tw-bg-slate-100 hover:tw-text-slate-900"
                      v-tippy="'Edit'"
                      @click="startEditRateCategory(rate)"
                    >
                      <font-awesome-icon icon="pen-to-square" class="tw-h-3.5 tw-w-3.5" />
                    </button>
                    <button
                      type="button"
                      class="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-text-slate-500 hover:tw-bg-rose-50 hover:tw-text-rose-700"
                      v-tippy="'Delete'"
                      @click="deleteRate(rate)"
                    >
                      <font-awesome-icon icon="trash" class="tw-h-3.5 tw-w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="tw-p-5 tw-text-sm tw-text-slate-500">No rates yet.</div>
      </section>

      <section class="tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white tw-p-5 tw-shadow-sm">
        <h3 class="tw-mb-2 tw-text-lg tw-font-semibold tw-text-slate-900">Billing</h3>
        <p class="tw-text-sm tw-text-slate-700">
          Total receivable:
          <span class="tw-font-semibold tw-text-slate-900">
            ${{ Number(totalReceivable || 0).toFixed(2) }}
          </span>
        </p>
      </section>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import Button from '@/components/core/Button.vue';
import EditableInputLabel from '@/components/core/Input/EditableInputLabel.vue';
import AddRateCategoryMutation from '@/gql/billing/mutations/AddContractRateCategory.gql';
import CustomerRates from '@/gql/customers/query/billing/CustomerRates.gql';
import DeleteRateCategoryMutation from '@/gql/billing/mutations/DeleteRateCategory.gql';
import EditRateCategoryMutation from '@/gql/billing/mutations/EditRateCategory.gql';

export default defineComponent({
  components: {
    Button,
    EditableInputLabel,
  },
  props: {
    customer: {
      required: true,
    },
  },
  data() {
    return {
      isAddingRate: false,
      newRate: {
        destinationCity: '',
        amount: '',
      },
      editingRate: {
        id: -1,
        rateType: undefined,
        rateName: '',
        amount: '',
        destinationCity: '',
        contractCustomerId: undefined,
      },
    };
  },
  methods: {
    normalizeToNumber(value: unknown): number {
      return typeof value === 'number' ? value : Number(value);
    },
    toggleAddContractRate() {
      const vm = (this as any);

      vm.isAddingRate = !vm.isAddingRate;
      if (!vm.isAddingRate) {
        vm.newRate = {
          destinationCity: '',
          amount: '',
        };
      }
    },
    startEditRateCategory(rate: any) {
      const vm = (this as any);

      vm.editingRate = {
        id: rate.id,
        rateType: rate.rateType,
        rateName: rate.rateName,
        amount: rate.amount,
        destinationCity: rate.destinationCity || '',
        contractCustomerId: (vm.customer as any).id,
      };
    },
    cancelEditRateCategory() {
      const vm = (this as any);

      vm.editingRate = {
        id: -1,
        rateType: undefined,
        rateName: '',
        amount: '',
        destinationCity: '',
        contractCustomerId: undefined,
      };
    },
    editRateCategory() {
      const vm = (this as any);
      const normalizedRate = {
        ...vm.editingRate,
        amount: vm.normalizeToNumber(vm.editingRate.amount),
      };

      if (Number.isNaN(normalizedRate.amount)) {
        console.error('Rate amount must be a valid number');
        return;
      }

      vm.$apollo
        .mutate({
          mutation: EditRateCategoryMutation,
          variables: normalizedRate,
        })
        .then((data) => {
          vm.cancelEditRateCategory();
          vm.$apollo.queries.contractRates.refetch();
          console.log(data);
        })
        .catch((err) => {
          console.error(err);
        });
    },
    deleteRate(rate) {
      const vm = (this as any);

      vm.$apollo
        .mutate({
          mutation: DeleteRateCategoryMutation,
          variables: {
            rateId: rate.id,
          },
        })
        .then((data) => {
          console.log(data);
          if (vm.editingRate.id === rate.id) {
            vm.cancelEditRateCategory();
          }
          vm.$apollo.queries.contractRates.refetch();
        })
        .catch((err) => {
          console.log(err);
        });
    },
    rateChargeString(rate: any): string {
      if (rate.rateType === 'quantity') {
        return `$${rate.amount.toFixed(2)} Per Charge`;
      } else if (rate.rateType === 'flat' || rate.rateType === 'base') {
        return `$${Number(rate.amount).toFixed(2)}`;
      } else {
        return `${rate.amount} %`;
      }
    },
    confirmContractRateAddition() {
      const vm = (this as any);
      const amount = vm.normalizeToNumber(vm.newRate.amount);

      if (Number.isNaN(amount)) {
        console.error('Rate amount must be a valid number');
        return;
      }

      vm.$apollo
        .mutate({
          mutation: AddRateCategoryMutation,
          variables: {
            rateType: 'base',
            amount,
            rateName: `Base Rate (${vm.newRate.destinationCity})`,
            destinationCity: vm.newRate.destinationCity,
            contractCustomerId: (vm.customer as any).id,
          },
        })
        .then((data) => {
          console.log(data);
          vm.$apollo.queries.contractRates.refetch();
          vm.newRate = {
            destinationCity: '',
            amount: '',
          };
          vm.isAddingRate = false;
        })
        .catch((err) => {
          console.log(err);
        });
    },
  },
  ...{
    apollo: {
      contractRates: {
        fetchPolicy: 'network-only',
        query: CustomerRates,
        skip() {
          return !(this as any).customer;
        },
        variables() {
          return {
            id: (this as any).customer.id,
          };
        },
        update: (data) =>
          (data.customer.contractRates || []).filter(
            (rate) => rate.isActive !== false,
          ),
      },
      totalReceivable: {
        query: CustomerRates,
        skip() {
          return !(this as any).customer;
        },
        variables() {
          return {
            id: (this as any).customer.id,
          };
        },
        update: (data) => data.customer.totalReceivable,
      },
    },
  },
});
</script>
